import { portalLanguagesData } from 'app/appSettings'
import { queryCreateObject, UpdateQueryResultSalesforce } from 'app/backend/Common'
import { Configuration } from 'app/backend/salesforce-maps/Configuration'
import { getTitleTranslation } from 'app/views/common/translationsCommon'
import moment from 'moment'
import pako from 'pako'
import { isValidJSON } from 'utils'
import { transformOldSfProps } from '../../../utils/transformOldSfProps'
import SFAuthService, { NO_USER } from '../SFAuthService'
import { mapSFToForm, reverseMap } from '../sfDataService'
import { textFields } from './sfSurvey'
import { getUsersByFlow } from './sfUser'

export const FORM_VERSION_ERROR = 'VERSION_ALREADY_EXISTS'
export const FORM_PREVIEW_CONFIG = 'FORM_PREVIEW_CONFIG'

export const FORM_TYPE_ELIGIBILITY_QUIZ = 'Eligibility quiz'

export const configurationTypes = {
  Contract: {
    name: 'CONTRACT'
  },
  'Application Type': {
    name: 'AVAILABLE_APPLICATIONS'
  }
}

export interface FormBackup {
  id: string
  data: any
  createdBy: string
  createdById: string
  portalVersion: string
  form: string
  createdDate: Date
}

export interface FormPageDetailed {
  [key: string]: any
  title: any
  translations: any
  holdsReusableElements?: boolean | null
  comments?: string | null
  origin?: string | null
  type?: string | null
  status?: string | null
  id: string
}

const formBackupMapFields = {
  direction: 'in',
  Id: 'id',
  Data__c: 'data',
  CreatedById: 'createdById',
  Portal_Version__c: 'portalVersion',
  Form_Page__c: 'form',
  CreatedDate: 'createdDate',
  Created_By__c: 'createdBy'
}
const reverseFormBackupMapFields = reverseMap(formBackupMapFields)

/**
 * Saves the preview configuration to Salesforce.
 * @function
 * @category Salesforce - Configuration__c
 * @param {object} config The preview configuration to save.
 * @returns {JSForceResult}
 */
export const savePreviewConfig = async (config) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Configuration__c')
    .find({ Name: FORM_PREVIEW_CONFIG })
    .then(async (result) => {
      if (result.length > 0) {
        const id = result[0].Id
        return await conn.sobject('Configuration__c').update({
          Id: id,
          Value__c: JSON.stringify(config)
        })
      } else {
        return await conn.sobject('Configuration__c').create({
          Name: FORM_PREVIEW_CONFIG,
          Value__c: JSON.stringify(config)
        })
      }
    })
}

/**
 * Fetches a form page from Salesforce based on the provided id.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {string} id The id of the form page to fetch.
 * @returns {FormPageDetailed | null}
 */
export const getFormPage = async (id: string): Promise<FormPageDetailed | null> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  if (id == null || id === '') {
    return null
  }
  return await conn
    .sobject('Form_Page__c')
    .find({ Id: id })
    .then(async (result) => {
      const form = result[0]
      if (!form) {
        return null
      }
      return await conn
        .sobject('SurveyTranslation__c')
        .find({
          Id: form.Title_Translation__c
        })
        .select('Id')
        .include('Survey_Texts__r')
        .select(textFields.join(', '))
        .end()
        .then((translations) => {
          const title =
            translations.length === 0
              ? Object.values(portalLanguagesData).reduce<{
                  portalTranslationId?: { id: string | null; text: string }
                }>((acc, langObj) => {
                  const { editKey } = langObj
                  acc[editKey] = {
                    id: null,
                    text: ''
                  }
                  return acc
                }, {})
              : Object.values(portalLanguagesData).reduce<{
                  portalTranslationId?: { id: string | null; text: string }
                }>((acc, langObj) => {
                  const { editKey, surveyTextKey } = langObj
                  const translation = translations[0].Survey_Texts__r.records.find((rec) =>
                    rec.Language__c.includes(surveyTextKey)
                  )
                  acc[editKey] = {
                    id: translation?.Id || null,
                    text: translation?.Text__c || ''
                  }
                  return acc
                }, {})
          const portalTranslation = translations[0]?.Survey_Texts__r.records.find(
            (rec) => rec.Text_Is_Portal_Translation_Id__c
          )
          if (portalTranslation) {
            title.portalTranslationId = {
              id: portalTranslation?.Id || null,
              text: portalTranslation?.Text__c || ''
            }
          }
          let toPass
          if (isValidJSON(form.Data__c)) {
            toPass = JSON.parse(form.Data__c)
          } else {
            const gzip = Buffer.from(form.Data__c, 'base64')
            toPass = pako.ungzip(gzip, { to: 'string' })
            toPass = JSON.parse(toPass)
            toPass = transformOldSfProps(toPass)
          }
          return {
            ...toPass,
            title,
            translations,
            holdsReusableElements: form.Holds_Reusable_Elements__c,
            comments: form.Comments__c,
            origin: form.Origin__c,
            type: form.Type__c,
            status: form.Status__c,
            id: form.Id
          }
        })
    })
}

export const getFormBackups = async (id: string): Promise<FormBackup[] | string> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Backup__c')
    .find({
      Form_Page__c: id
    })
    .then((result) => {
      return result.map((obj) => ({
        ...mapSFToForm(formBackupMapFields, obj),
        data: uncompressFormData(obj.Data__c)
      }))
    })
}

/**
 * Updates a form page in Salesforce with the provided data.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {object} toUpdate The data to update the form page with.
 * @returns {JSForceResult}
 */
export const updateFormPage = async (toUpdate: object) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn.sobject('Form_Page__c').update({
    ...toUpdate
  })
}

interface ConnectedObjectData {
  identId: string
  loadOrder: number
  name: string
  type: string
}

export interface FormConfigInterface {
  id: string
  config: FormData
}

export interface FormData {
  sections?: any[]
  injectableComponents?: any[]
  autosave?: boolean
  version?: number
  type?: string
  status?: string
  enableMultiuser?: boolean
  pdfProps?: any
  style?: any
  formType?: string
  showSectionTitlesAsHeaders?: boolean
  showFormTitleInPdf?: boolean
  renderElementsWithTitles?: boolean
  hideFormTitle?: boolean
  doNotUseStickyHeader?: boolean
  showPrintButton?: boolean
  objectsConnected?: ConnectedObjectData[]
  displaySaveFailedDialog?: boolean
  displayUnsavedWarning?: boolean
  displayesSavedInMeantimeWarning?: boolean
  restrictAccessForRoles?: boolean
  restrictAccessType?: string
  translatedFor?: string
  overrideFormLanguage?: boolean
  title?: Record<string, { id: string | null; text: string }>
  translations?: any[]
  id?: string | undefined
  comments?: string | undefined
  origin?: string | undefined
  supportedFormType: string[]
}

export interface FormPageSummary {
  name: {
    [langKey: string]: string
  }
  lastModifiedBy?: string
  lastModifiedDate: string
  comments?: string
  origin?: string
  copyOf?: string
  id: string
  config: FormData
  status?: string
  type?: string
}

export interface ReusableFormPage {
  id: string
  config: FormData
}

/**
 * Compresses form data into a compressed string representation.
 * @function
 * @category - Salesforce - Form_Page__c
 * @param {FormData} data - The form data to compress.
 * @returns {string} The compressed form data encoded as a base64 string.
 */
export const compressFormData = (data: FormData): string => {
  const toSave = {
    sections: data.sections,
    injectableComponents: data.injectableComponents,
    autosave: data.autosave,
    version: data.version,
    enableMultiuser: data.enableMultiuser,
    pdfProps: data.pdfProps,
    style: data.style,
    formType: data.formType,
    showSectionTitlesAsHeaders: data.showSectionTitlesAsHeaders,
    showFormTitleInPdf: data.showFormTitleInPdf,
    renderElementsWithTitles: data.renderElementsWithTitles,
    hideFormTitle: data.hideFormTitle,
    doNotUseStickyHeader: data.doNotUseStickyHeader,
    showPrintButton: data.showPrintButton,
    objectsConnected: data.objectsConnected,
    displaySaveFailedDialog: data.displaySaveFailedDialog,
    displayUnsavedWarning: data.displayUnsavedWarning,
    displayesSavedInMeantimeWarning: data.displayesSavedInMeantimeWarning,
    restrictAccessForRoles: data.restrictAccessForRoles,
    restrictAccessType: data.restrictAccessType,
    translatedFor: data.translatedFor,
    overrideFormLanguage: data.overrideFormLanguage,
    supportedFormType: data.supportedFormType
  }
  const json = JSON.stringify(toSave)
  const gzip = pako.gzip(json)
  const b64 = Buffer.from(gzip).toString('base64')
  return b64
}

/**
 * Uncompresses compressed form data into its original JSON representation.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {string} data The compressed form data encoded as a base64 string.
 * @returns {FormData} The uncompressed form data as a JavaScript object.
 */
export const uncompressFormData = (data: string) => {
  const gzip = Buffer.from(data, 'base64')
  const uncompressed = pako.ungzip(gzip, { to: 'string' })
  return JSON.parse(uncompressed)
}

interface UpdateObject {
  Id: string | undefined
  Comments__c: string | undefined
  Data__c: string
  Name?: string
  Status__c: string | undefined
  Type__c: string | undefined
}

/**
 * Saves a form page with the provided data in Salesforce.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {FormData} data The data of the form page to be saved.
 * @returns {JSForceResult}
 */
export const saveFormPage = async (data: FormData) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  console.log('data: ', data)
  const updatePromise = async () => {
    const saveTitle: object[] = []
    const translation = data.translations != null ? data.translations[0] : null
    if (!translation) {
      return await conn
        .sobject('SurveyTranslation__c')
        .create({})
        .then(async (translation) => {
          const updates: any[] = []
          Object.values(portalLanguagesData).forEach((langObj) => {
            const { editKey, surveyTextKey } = langObj
            saveTitle.push(
              conn.sobject('Survey_Text__c').create({
                Survey_Translation__c: translation.id,
                Text__c: data.title?.[editKey]?.text,
                Language__c: surveyTextKey
              })
            )
          })
          updates.push(
            conn.sobject('Form_Page__c').update({
              Id: data.id,
              Title_Translation__c: translation.id,
              Status__c: data.status,
              Type__c: data.type,
              Comments__c: data.comments,
              Data__c: compressFormData(data)
            })
          )
          return await Promise.all(updates)
        })
    } else {
      Object.values(portalLanguagesData).forEach((langObj) => {
        const { editKey, surveyTextKey } = langObj
        if (data.title?.[editKey]?.id) {
          const update = conn.sobject('Survey_Text__c').update({
            Id: data.title?.[editKey]?.id,
            Text__c: data.title?.[editKey]?.text
          })
          saveTitle.push(update)
        } else {
          const create = conn.sobject('Survey_Text__c').create({
            Survey_Translation__c: translation.Id,
            Text__c: data.title?.[editKey]?.text,
            Language__c: surveyTextKey
          })
          saveTitle.push(create)
        }
      })
    }
    const compressedData = compressFormData(data)
    const updateObject: UpdateObject = {
      Id: data.id,
      Comments__c: data.comments,
      Data__c: compressedData,
      Status__c: data.status,
      Type__c: data.type
    }
    if (data.title?.en.text) {
      updateObject.Name = data.title?.en.text
    }
    return await Promise.all([
      conn.sobject('Form_Page__c').update(updateObject),
      ...saveTitle
    ]).then(async (result) => {
      return await conn.sobject('Form_Backup__c').create({
        Data__c: compressedData,
        Form_Page__c: data.id,
        Portal_Version__c: process.env.REACT_APP_VERSION
      })
    })
  }

  if (data.origin) {
    return await checkIfFormVersionExists({
      origin: data.origin,
      id: data.id,
      version: data.version as number
    }).then(async (result) => {
      return await updatePromise()
    })
  } else {
    return await updatePromise()
  }
}

/**
 * Checks the next available form version based on the origin, and current version.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {string} origin The origin of the form.
 * @param {string} [copyOf] The id of the form page that the new form is a copy of (optional).
 * @param {number} [version] The current version of the form (optional).
 * @returns {number} The next available form version.
 */
export const checkNextAvaliableFormVersion = async ({
  origin,
  copyOf,
  version
}: {
  origin: string
  version?: number
  copyOf?: string
}) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Page__c')
    .find({
      $or: [
        { Origin__c: copyOf },
        { Copy_of__c: copyOf },
        { Origin__c: origin },
        { Copy_of__c: origin }
      ]
    })
    .then((forms) => {
      const versions = forms.map((form) => {
        let savedData
        if (isValidJSON(form.Data__c)) {
          savedData = JSON.parse(form.Data__c)
        } else {
          const gzip = Buffer.from(form.Data__c, 'base64')
          savedData = pako.ungzip(gzip, { to: 'string' })
          savedData = JSON.parse(savedData)
        }
        return savedData.version ? +savedData.version : 0
      })
      const newVersion = Math.max(...versions, version || 0) + 1
      return newVersion
    })
}

/**
 * Checks if a form version already exists based on the origin, form id, and version number.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {string} origin The origin of the form.
 * @param {string} [id] The id of the form page. Optional, used to exclude the current form from the check.
 * @param {number} version The version number of the form.
 * @returns {Promise<void>} A Promise that resolves if the form version does not exist, and rejects with an error message if it does.
 */
export const checkIfFormVersionExists = async ({
  origin,
  id,
  version
}: {
  origin: string
  id?: string
  version: number
}) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Page__c')
    .find({
      $or: [{ Origin__c: origin }, { Copy_of__c: origin }]
    })
    .then(async (forms) => {
      const versionAlready = forms.some((form) => {
        let savedData
        if (isValidJSON(form.Data__c)) {
          savedData = JSON.parse(form.Data__c)
        } else {
          const gzip = Buffer.from(form.Data__c, 'base64')
          savedData = pako.ungzip(gzip, { to: 'string' })
          savedData = JSON.parse(savedData)
        }
        if (id) {
          return +savedData.version === +version && form.Id !== id
        } else {
          return +savedData.version === +version
        }
      })
      if (versionAlready) {
        return await Promise.reject(FORM_VERSION_ERROR)
      } else {
        return await Promise.resolve()
      }
    })
}

/**
 * Retrieves a reusable form page from Salesforce (its id and configuration).
 * @function
 * @category Salesforce - Form_Page__c
 * @returns {Promise<{ id: string, config: any }>}
 */
export const getReusableFormPage = async (): Promise<ReusableFormPage> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Page__c')
    .find({
      Holds_Reusable_Elements__c: true
    })
    .select('Id, Data__c')
    .then((result) => {
      const form = result[0]
      let toPass
      if (isValidJSON(form.Data__c)) {
        toPass = JSON.parse(form.Data__c)
      } else {
        const gzip = Buffer.from(form.Data__c, 'base64')
        toPass = pako.ungzip(gzip, { to: 'string' })
        toPass = JSON.parse(toPass)
      }
      return {
        id: form.Id,
        config: toPass || {}
      }
    })
}

/**
 * Retrieves form pages from Salesforce.
 * @function
 * @category Salesforce - Form_Page__c
 * @returns {FormPageSummary[]}
 */
export const getFormPages = async (searchProps = {}): Promise<FormPageSummary[]> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Page__c')
    .find({
      Holds_Reusable_Elements__c: false,
      App_Version__c: { $ne: '002_Post_FC' },
      ...searchProps
    })
    .select(
      'Id, Title_Translation__c, Origin__c, Data__c, Comments__c, LastModifiedDate, LastModifiedById, Type__c, Status__c, Copy_of__c'
    )
    .run({ autoFetch: true, maxFetch: 1000 })
    .then(
      async (result: any[]) => {
        const matchObj = {}
        const userMatchObj = {}
        const translationIds: string[] = []
        const lastModifiedUsers: string[] = []
        result.forEach((obj) => {
          translationIds.push(obj.Title_Translation__c)
          matchObj[obj.Title_Translation__c] = obj
          if (!lastModifiedUsers.includes(obj.LastModifiedById)) {
            lastModifiedUsers.push(obj.LastModifiedById)
          }
        })
        const translationQuery = async (translationIds: string[]): Promise<any> =>
          await conn
            .sobject('SurveyTranslation__c')
            .find({
              Id: { $in: translationIds }
            })
            .select('Id')
            .include('Survey_Texts__r')
            .select(textFields.join(', '))
            .end()
            .run({ autoFetch: true, maxFetch: 10000 })
        return await Promise.all([
          translationQuery(translationIds),
          getUsersByFlow(lastModifiedUsers)
        ]).then(([translations, users = []]) => {
          users.forEach((user) => {
            userMatchObj[user.Id] = user.Name
          })

          const translationsMatchObject = {}
          const unhandledTranslationIds: string[] = []

          translationIds.forEach((id) => {
            const translation = translations.find((trans) => trans.Id === id)
            if (translation != null) {
              translationsMatchObject[id] = translation
            } else {
              unhandledTranslationIds.push(id)
            }
          })

          return (
            result
              // filter out forms with corrupted Data__c field
              .filter((form) => {
                try {
                  if (isValidJSON(form.Data__c)) {
                    JSON.parse(form.Data__c)
                  } else {
                    const gzip = Buffer.from(form.Data__c, 'base64')
                    const unzipped = pako.ungzip(gzip, { to: 'string' })
                    JSON.parse(unzipped)
                  }
                } catch (e) {
                  console.warn(`The Data__c field of the form with id ${form.Id} is corrupted`)
                  return false
                }
                return true
              })
              .map((form): FormPageSummary => {
                const translation = translationsMatchObject[form.Title_Translation__c]
                let toPass
                if (isValidJSON(form.Data__c)) {
                  toPass = JSON.parse(form.Data__c)
                } else {
                  const gzip = Buffer.from(form.Data__c, 'base64')
                  toPass = pako.ungzip(gzip, { to: 'string' })
                  toPass = JSON.parse(toPass)
                }

                const titleTranslation = Object.keys(portalLanguagesData).reduce(
                  (acc, lang) => {
                    acc.name[portalLanguagesData[lang].editKey] = getTitleTranslation(
                      lang,
                      translation
                    )
                    return acc
                  },
                  { name: {} }
                )

                const formPage: FormPageSummary = {
                  ...titleTranslation,
                  lastModifiedBy: userMatchObj[form.LastModifiedById],
                  lastModifiedDate: form.LastModifiedDate,
                  comments: form.Comments__c,
                  origin: form.Origin__c,
                  copyOf: form.Copy_of__c,
                  id: form.Id,
                  config: toPass ?? {},
                  status: form.Status__c,
                  type: form.Type__c
                }
                return formPage
              })
              .filter((item) => item)
          )
        })
      },
      (reject) => {
        console.log('qqq', reject)
      }
    )
}

interface Title {
  [language: string]: string
}

export interface MinimalFormData {
  status?: string
  type?: string
  comments?: string
  autosave?: boolean
  version?: number
  sections?: unknown[]
}

/**
 * Creates a new form page in Salesforce.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {Title} title The title of the form page in multiple languages.
 * @param {string} origin The origin of the form page.
 * @param {boolean} [isCopy] - Whether the form page is a copy of an existing form page (optional).
 * @param {object} [options.data={}] - Additional data for the form page (optional).
 * @returns {JSForceResult}
 */
export const createFormPage = async ({
  title,
  origin,
  isCopy,
  data = {}
}: {
  title: Title
  origin?: string
  isCopy?: boolean
  data: MinimalFormData
}): Promise<UpdateQueryResultSalesforce[]> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }

  const json = JSON.stringify(data)
  const gzip = pako.gzip(json)
  const b64 = Buffer.from(gzip).toString('base64')

  const translation = await queryCreateObject({
    type: 'SurveyTranslation',
    createData: {}
  })

  const transTextPromises = Object.values(portalLanguagesData)
    .map(async (obj) => {
      const { editKey, surveyTextKey } = obj
      const text = title?.[editKey]
      if (typeof text === 'string' && text.trim() !== '') {
        return await queryCreateObject({
          type: 'SurveyText',
          createData: {
            surveyTranslationId: translation.id,
            text,
            language: surveyTextKey
          }
        })
      }
      return null
    })
    .filter(Boolean) as Array<Promise<UpdateQueryResultSalesforce>>

  const formPagePromise = queryCreateObject({
    type: 'FormPage',
    createData: {
      titleTranslation: translation.id,
      ...(isCopy === true
        ? {
            copyOf: origin,
            status: data.status,
            type: data.type,
            comments: data.comments
          }
        : {
            origin
          }),
      data: b64
    }
  })

  return await Promise.all([...transTextPromises, formPagePromise])
}

/**
 * Deletes a form page from Salesforce.
 * If the form page is associated with an origin, and deleting it leaves no other form pages with the same origin,
 * the origin field is also cleared for the remaining form page.
 * @function
 * @category Salesforce - Form_Page__c
 * @param {string} id The id of the form page to delete.
 * @param {string} [origin] The origin of the form page (optional). If provided, checks if the origin is associated with other form pages.
 * @returns {JSForceResult}
 */
export const deleteFormPage = async (id: string, origin?: string) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Form_Page__c')
    .delete(id)
    .then(async (result) => {
      if (!origin) {
        return result
      } else {
        return await conn
          .sobject('Form_Page__c')
          .find({
            $or: [{ Origin__c: origin }, { Copy_of__c: origin }]
          })
          .then(async (forms) => {
            if (forms.length === 1) {
              return await conn.sobject('Form_Page__c').update({
                Id: forms[0].Id,
                Origin__c: null,
                Copy_of__c: null
              })
            } else {
              return result
            }
          })
      }
    })
}

/**
 * Retrieves configurations from Salesforce.
 * @function
 * @category Salesforce - Configuration__c
 * @returns {Configuration[]}
 */
export const getConfigurations = async (): Promise<Configuration[]> => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn.sobject('Configuration__c').find({})
}

/**
 * Retrieves a single Configuration from Salesforce based on the provided search properties.
 * @function
 * @category Salesforce - Configuration__c
 * @param {object} [searchProps={}] The search properties to filter the configuration. If it is not provided, it will default to an empty object and all Configuration objects will be fetched.
 * @returns {Configuration | Configuration[]}
 */
export const getConfiguration = async (searchProps: object = {}) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Configuration__c')
    .find({ ...searchProps })
    .then((result) => result[0])
}

/**
 * Retrieves application configurations from Salesforce.
 * @function
 * @category Salesforce - Configuration__c
 * @returns {Configuration[]}
 */
export const getApplicationConfigurations = async () => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn
    .sobject('Configuration__c')
    .find({})
    .then((result) => {
      return result
        .filter((item) => item.Name === 'AVAILABLE_APPLICATIONS')
        .map((item) => ({ ...JSON.parse(item.Value__c), id: item.Id }))
    })
}

/**
 * Deletes an application configuration from Salesforce.
 * @function
 * @category Salesforce - Configuration__c
 * @param {string} id The id of the application configuration to delete.
 * @returns {JSForceResult}
 */
export const deleteApplicationConfig = async (id: string) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  return await conn.sobject('Configuration__c').delete(id)
}

interface ConfigListItem {
  id?: string
  expanded?: boolean
  type?: string
  name: string
  subtitle: string
  description?: string
  notes?: string
  form?: string | null
  quiz?: string | null
  printForm?: string | null
  order?: number
  startTime?: moment.Moment | null
  endTime?: moment.Moment | null
  reminderTime?: moment.Moment | null
  disableInProvinces?: string[]
  avaliableInLanguages?: string[]
  inTesting?: boolean
  obsolete?: boolean
  moreInfoUrl?: string
  logo?: string
}

interface SaveApplicationConfigData {
  configList: ConfigListItem[]
}

/**
 * Saves application configurations to Salesforce.
 * @function
 * @category Salesforce - Configuration__c
 * @param {SaveApplicationConfigData} data The data containing the configurations to save.
 * @returns {Promise<Object[]>} A promise that resolves to an array of objects representing the saved configurations.
 */
export const saveApplicationConfigWithUpdateByFlow = async (data: SaveApplicationConfigData) => {
  const conn = SFAuthService.getConnection()
  if (conn == null) {
    return await Promise.reject(NO_USER)
  }
  const toUpdate: object[] = []
  const toCreate: object[] = []
  data.configList.forEach((dataToSave, index) => {
    const item = { ...dataToSave }
    delete item.expanded
    item.order = index
    if (item.startTime != null) {
      const date = moment.utc(item.startTime)
      const local = date.local(true)
      item.startTime = moment.utc(local)
    }
    if (item.endTime != null) {
      const date = moment.utc(item.endTime)
      const local = date.local(true)
      item.endTime = moment.utc(local)
    }
    if (item.reminderTime != null) {
      const date = moment.utc(item.reminderTime)
      const local = date.local(true)
      item.reminderTime = moment.utc(local)
    }
    const objName = item.type && configurationTypes[item.type].name
    if (!item.id) {
      toCreate.push({
        Value__c: JSON.stringify(item),
        Name: objName
      })
    } else {
      const sfId = item.id
      delete item.id
      toUpdate.push({
        Id: sfId,
        Value__c: JSON.stringify(item),
        Name: objName
      })
    }
  })
  return await Promise.all([
    conn.sobject('Configuration__c').create(toCreate),
    conn.requestPost('/actions/custom/flow/App_Update_Configurations', {
      inputs: [
        {
          toUpdate
        }
      ]
    })
  ])
}
